import React, { useState, useRef, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import styled from "styled-components";
import { FaShoppingBag, FaUser, FaBars, FaTimes } from "react-icons/fa";
import { useStore, useCartStore } from "../../store/useStore";
import { useAuth } from "../../contexts/AuthContext";
import SignOutButton from "../Auth/SignOutButton";
import { productService } from "../../api/productService";

const CartIconWrapper = styled.div`
  position: relative;
  display: inline-block;
`;

const CartCountBadge = styled.span`
  position: absolute;
  top: -8px;
  right: -10px;
  color: #fff;
  font-size: 0.95rem;
  font-weight: bold;
  padding: 0;
  min-width: 22px;
  text-align: center;
  background: none;
  border-radius: 0;
  box-shadow: none;
  pointer-events: none;

  @media (max-width: 768px) {
    font-size: 0.9rem;
    min-width: 20px;
    top: -6px;
    right: -8px;
  }

  @media (max-width: 480px) {
    font-size: 0.85rem;
    min-width: 18px;
    top: -5px;
    right: -7px;
  }
`;

const NavbarContainer = styled.nav`
  width: 100%;
  background: #e97d7dff;
  box-shadow: 0 2px 8px rgba(233, 125, 125, 0.13);
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 2rem;
  height: 72px;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;

  @media (max-width: 1024px) {
    padding: 0 1.5rem;
  }

  @media (max-width: 768px) {
    padding: 0 1rem;
    height: 64px;
  }

  @media (max-width: 480px) {
    padding: 0 0.8rem;
    height: 60px;
  }
`;

const LogoImg = styled.img`
  height: 70px;
  width: auto;
  display: block;

  @media (max-width: 1024px) {
    height: 70px;
  }

  @media (max-width: 768px) {
    height: 70px;
  }

  @media (max-width: 480px) {
    height: 70px;
  }
`;

const Logo = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;

  @media (max-width: 768px) 
    justify-content: flex-start; /* Logo on the left */
    order: 0; /* Logo first */
  }
`;

const Menu = styled.ul<{ isOpen: boolean }>`
  display: flex;
  gap: 2rem;
  list-style: none;
  margin: 0;
  padding: 0;

  @media (max-width: 480px) {
    display: none; /* Hide navigation links on mobile only */
  }
`;

const MenuItem = styled.li`
  font-size: 1.1rem;
  color: #111;
  cursor: pointer;
  transition: color 0.2s;

  &:hover {
    color: #fff;
  }

  @media (max-width: 768px) {
    font-size: 1.3rem;
    padding: 0.5rem 1rem;
    border-radius: 0.5rem;
    transition: all 0.2s;

    &:hover {
      background: rgba(255, 255, 255, 0.1);
      color: #fff;
    }
  }

  @media (max-width: 480px) {
    font-size: 1.2rem;
    padding: 0.4rem 0.8rem;
  }
`;

const MenuItemLink = styled(Link)`
  color: inherit;
  text-decoration: none;
  display: block;
  width: 100%;
  height: 100%;
`;

const UtilityIcons = styled.div`
  display: flex;
  gap: 1.5rem;
  align-items: center;
  color: #111;
  font-size: 1.3rem;

  & > *:hover {
    color: #fff;
  }

  @media (max-width: 480px) {
    gap: 1rem;
  }

  @media (max-width: 768px) {
    gap: 1rem;
    font-size: 1.2rem;
    order: 2; /* Icons on the right */
  }

  @media (max-width: 480px) {
    gap: 0.8rem;
    font-size: 1.1rem;
  }
`;

const HamburgerButton = styled.button`
  display: none;
  background: none;
  border: none;
  color: #111;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 0.3rem;
  transition: all 0.2s;
  order: -1;

  &:hover {
    color: #fff;
    background: rgba(255, 255, 255, 0.1);
  }

  @media (max-width: 480px) {
    display: block;

    margin-top: 4px;
    font-size: 1.3rem;
    padding: 0.4rem;
  }
`;

const UserIconWrapper = styled.div<{ isAuthenticated: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  margin-top: ${(props) => (props.isAuthenticated ? "20px" : "0")};
  @media (max-width: 768px) {
    margin-top: 0px;
  }

  @media (max-width: 480px) {
    display: none; /* Hide user icon on mobile */
  }
`;

const EmailSnippet = styled.div`
  font-size: 0.95rem;
  color: #fff;
  margin-top: 0.1rem;
  letter-spacing: 0.02em;
  max-width: 90px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  @media (max-width: 768px) {
    display: none; /* Hide email snippet on mobile screens */
  }
`;

// Dropdown removed - using side sheet for all screen sizes

const SideSheet = styled.div<{ isOpen: boolean }>`
  display: block;
  position: fixed;

  @media (max-width: 480px) {
    display: none; /* Hide side sheet on mobile */
  }
  top: 64px;
  right: 0;
  width: 320px;
  height: calc(100vh - 64px);
  background: #fff;
  box-shadow: -2px 0 12px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  padding: 2rem 1.5rem;
  transform: ${(props) =>
    props.isOpen ? "translateX(0)" : "translateX(100%)"};
  opacity: ${(props) => (props.isOpen ? "1" : "0")};
  transition: transform 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94),
    opacity 0.3s ease-in-out;

  @media (max-width: 768px) {
    width: 280px;
  }

  @media (max-width: 480px) {
    width: 260px;
    padding: 1.5rem 1rem;
  }
`;

const SideSheetOverlay = styled.div<{ isOpen: boolean }>`
  display: block;
  position: fixed;
  top: 64px;
  left: 0;
  width: 100%;
  height: calc(100vh - 64px);
  background: rgba(0, 0, 0, 0.5);
  z-index: 999;
  opacity: ${(props) => (props.isOpen ? "1" : "0")};
  visibility: ${(props) => (props.isOpen ? "visible" : "hidden")};
  transition: opacity 0.3s ease-in-out, visibility 0.3s ease-in-out;

  @media (max-width: 480px) {
    display: none; /* Hide side sheet overlay on mobile */
  }
`;

const SideSheetHeader = styled.div<{ isOpen?: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #eee;
  transform: ${(props) =>
    props.isOpen ? "translateY(0)" : "translateY(-20px)"};
  opacity: ${(props) => (props.isOpen ? "1" : "0")};
  transition: transform 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94) 0.1s,
    opacity 0.3s ease-in-out 0.1s;

  @media (max-width: 480px) {
    margin-bottom: 1.5rem;
    padding-bottom: 0.8rem;
  }
`;

const SideSheetTitle = styled.h3`
  font-size: 1.2rem;
  font-weight: 600;
  color: #333;
  margin: 0;

  @media (max-width: 480px) {
    font-size: 1.1rem;
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  color: #666;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 0.3rem;
  transition: all 0.2s;

  &:hover {
    background: #f5f5f5;
    color: #333;
  }
`;

const UserInfo = styled.div<{ isOpen?: boolean }>`
  margin-bottom: 2rem;
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 0.8rem;
  transform: ${(props) =>
    props.isOpen ? "translateY(0)" : "translateY(-20px)"};
  opacity: ${(props) => (props.isOpen ? "1" : "0")};
  transition: transform 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94) 0.2s,
    opacity 0.3s ease-in-out 0.2s;

  @media (max-width: 480px) {
    margin-bottom: 1.5rem;
    padding: 0.8rem;
  }
`;

const UserEmail = styled.div`
  font-size: 1rem;
  color: #333;
  font-weight: 500;
  margin-bottom: 0.5rem;

  @media (max-width: 480px) {
    font-size: 0.95rem;
  }
`;

const UserStatus = styled.div`
  font-size: 0.9rem;
  color: #666;

  @media (max-width: 480px) {
    font-size: 0.85rem;
  }
`;

const SideSheetActions = styled.div<{ isOpen?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  transform: ${(props) =>
    props.isOpen ? "translateY(0)" : "translateY(-20px)"};
  opacity: ${(props) => (props.isOpen ? "1" : "0")};
  transition: transform 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94) 0.3s,
    opacity 0.3s ease-in-out 0.3s;
`;

const ActionButton = styled.button<{ variant?: "primary" | "secondary" }>`
  width: 100%;
  background: ${(props) =>
    props.variant === "primary" ? "#e97d7d" : "#f8f9fa"};
  color: ${(props) => (props.variant === "primary" ? "#fff" : "#333")};
  border: ${(props) =>
    props.variant === "secondary" ? "1px solid #e0e0e0" : "none"};
  border-radius: 0.8rem;
  padding: 1rem;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;

  &:hover {
    background: ${(props) =>
      props.variant === "primary" ? "#d96a6a" : "#e9ecef"};
    transform: translateY(-1px);
  }

  @media (max-width: 480px) {
    padding: 0.8rem;
    font-size: 0.95rem;
  }
`;

const LogoutButton = styled.button`
  width: 100%;
  background: #e97d7d;
  color: #fff;
  border: none;
  border-radius: 0.8rem;
  padding: 1rem;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #d96a6a;
    transform: translateY(-1px);
  }

  @media (max-width: 480px) {
    padding: 0.8rem;
    font-size: 0.95rem;
  }
`;

// Mobile Menu Sidesheet Components
const MobileMenuSidesheet = styled.div<{ isOpen: boolean }>`
  display: none;

  @media (max-width: 480px) {
    display: block;
    position: fixed;
    top: 0;
    right: 0;
    width: 370px;
    height: 100vh;
    background: #fff;
    box-shadow: -2px 0 16px rgba(233, 125, 125, 0.13);
    z-index: 2001;
    transform: translateX(${(props) => (props.isOpen ? "0" : "100%")});
    transition: transform 0.45s cubic-bezier(0.4, 1.2, 0.6, 1);
  }
`;

const MobileMenuBackdrop = styled.div<{ isOpen: boolean }>`
  display: none;

  @media (max-width: 480px) {
    display: ${(props) => (props.isOpen ? "block" : "none")};
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.18);
    z-index: 2000;
  }
`;

const MobileMenuCloseBtn = styled.button`
  position: absolute;
  top: 1.1rem;
  right: 1.1rem;
  background: none;
  border: none;
  font-size: 1.7rem;
  color: #e97d7dff;
  cursor: pointer;
  z-index: 10;
  transition: color 0.2s;
  &:hover {
    color: #000;
  }
`;

const MobileMenuContent = styled.div`
  padding: 2.5rem 1.5rem 2rem 1.5rem;
  display: flex;
  flex-direction: column;
  height: calc(100vh - 80px);
  overflow-y: auto;
`;

const MobileMenuSection = styled.div`
  margin-bottom: 2rem;

  &:last-child {
    margin-bottom: 0;
  }
`;

const MobileMenuSectionTitle = styled.h3`
  font-size: 1.1rem;
  font-weight: 600;
  color: #666;
  margin: 0 0 1rem 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const MobileMenuCategories = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const MobileMenuCategory = styled(Link)`
  width: 100%;
  padding: 1rem;
  background: #f7f7f7;
  color: #222;
  border: none;
  border-radius: 0.7rem;
  font-size: 1.08rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  text-align: left;
  margin-bottom: 0.5rem;
  box-shadow: 0 1px 6px rgba(233, 125, 125, 0.07);
  text-decoration: none;
  display: block;

  &:hover {
    background: #e97d7dff;
    color: #fff;
    transform: translateY(-1px);
  }
`;

const MobileMenuActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const MobileMenuAction = styled(Link)`
  width: 100%;
  padding: 1rem;
  background: #f7f7f7;
  color: #222;
  border: none;
  border-radius: 0.7rem;
  font-size: 1.08rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  text-align: left;
  margin-bottom: 0.5rem;
  box-shadow: 0 1px 6px rgba(233, 125, 125, 0.07);
  text-decoration: none;
  display: block;

  &:hover {
    background: #e97d7dff;
    color: #fff;
    transform: translateY(-1px);
  }
`;

const MobileMenuActionButton = styled.button`
  width: 100%;
  padding: 1rem;
  background: #f7f7f7;
  color: #222;
  border: none;
  border-radius: 0.7rem;
  font-size: 1.08rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  text-align: left;
  margin-bottom: 0.5rem;
  box-shadow: 0 1px 6px rgba(233, 125, 125, 0.07);
  text-decoration: none;
  display: block;

  &:hover {
    background: #e97d7dff;
    color: #fff;
    transform: translateY(-1px);
  }
`;

const MobileMenuUserInfo = styled.div`
  background: #f7f7f7;
  border-radius: 0.7rem;
  padding: 1rem;
  margin-bottom: 1rem;
  box-shadow: 0 1px 6px rgba(233, 125, 125, 0.07);
`;

const MobileMenuUserEmail = styled.div`
  font-size: 1.08rem;
  font-weight: 600;
  color: #222;
  margin-bottom: 0.25rem;
`;

const MobileMenuUserStatus = styled.div`
  font-size: 0.9rem;
  color: #666;
`;

// Cart-related styled components removed - using existing cart drawer instead

const Navbar: React.FC = () => {
  const navigate = useNavigate();
  const { setCartOpen } = useStore();
  const cartCount = useCartStore((state) =>
    state.items.reduce((sum, i) => sum + i.qty, 0)
  );
  const { user } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [sideSheetOpen, setSideSheetOpen] = useState(false);
  const [categories, setCategories] = useState<string[]>([]);
  // Removed sideSheetMode - only using side sheet for user account now
  const userIconRef = useRef<HTMLDivElement>(null);
  const { setOnItemAdded } = useCartStore();

  // Set up callback to show cart drawer when item is added to cart
  useEffect(() => {
    setOnItemAdded(() => {
      setCartOpen(true);
    });
  }, [setOnItemAdded, setCartOpen]);

  // Fetch categories for mobile menu
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const products = await productService.getAllProducts();
        const uniqueCategories = Array.from(
          new Set(products.map((product) => product.category))
        ).filter(Boolean);
        setCategories(uniqueCategories);
      } catch (error) {
        console.error("Failed to fetch categories:", error);
      }
    };

    fetchCategories();
  }, []);

  // Close mobile menu and side sheet on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      // Close mobile menu when clicking outside
      if (mobileMenuOpen) {
        const target = e.target as Element;
        if (!target.closest("nav") && !target.closest("[data-mobile-menu]")) {
          setMobileMenuOpen(false);
        }
      }

      // Close side sheet when clicking outside
      if (sideSheetOpen) {
        const target = e.target as Element;
        if (!target.closest("[data-side-sheet]")) {
          setSideSheetOpen(false);
        }
      }
    };

    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [mobileMenuOpen, sideSheetOpen]);

  const handleUserIconClick = () => {
    if (user) {
      // Check if we're on mobile (screen width <= 480px)
      if (window.innerWidth <= 480) {
        // On mobile, open the mobile menu instead of side sheet
        setMobileMenuOpen(true);
      } else {
        // On desktop/tablet, open side sheet
        setSideSheetOpen(true);
      }
    } else {
      navigate("/signin");
    }
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const handleMenuItemClick = (path: string) => {
    navigate(path);
    setMobileMenuOpen(false);
  };

  const closeSideSheet = () => {
    setSideSheetOpen(false);
  };

  const handleLogout = () => {
    setSideSheetOpen(false);
    // The SignOutButton component will handle the actual logout
  };

  return (
    <>
      <NavbarContainer>
        <Logo>
          <LogoImg
            src="/craft-bin1.PNG"
            alt="Artisan's Nook Logo"
            onClick={() => navigate("/")}
          />
        </Logo>

        <Menu isOpen={mobileMenuOpen} data-mobile-menu>
          <MenuItem>
            <MenuItemLink
              to="/shop"
              onClick={() => handleMenuItemClick("/shop")}
            >
              Shop
            </MenuItemLink>
          </MenuItem>
          <MenuItem>
            <MenuItemLink
              to="/aboutus"
              onClick={() => handleMenuItemClick("/aboutus")}
            >
              About Us
            </MenuItemLink>
          </MenuItem>
          {/* <MenuItem onClick={() => handleMenuItemClick("/shop")}>Custom Orders</MenuItem> */}
          <MenuItem>
            <MenuItemLink
              to="/contact"
              onClick={() => handleMenuItemClick("/contact")}
            >
              Contact
            </MenuItemLink>
          </MenuItem>
        </Menu>

        <UtilityIcons>
          <UserIconWrapper ref={userIconRef} isAuthenticated={!!user}>
            {
              FaUser({
                title: user ? user.email || "User" : "Sign In",
                style: { cursor: "pointer" },
                onClick: handleUserIconClick,
              }) as React.ReactElement
            }
            {user && (
              <EmailSnippet>
                {user.email
                  ? user.email.slice(0, 8) +
                    (user.email.length > 8 ? "..." : "")
                  : "User"}
              </EmailSnippet>
            )}
          </UserIconWrapper>
          <CartIconWrapper>
            {
              FaShoppingBag({
                title: "Cart",
                style: { cursor: "pointer" },
                onClick: () => setCartOpen(true),
              }) as React.ReactElement
            }
            {cartCount > 0 && <CartCountBadge>{cartCount}</CartCountBadge>}
          </CartIconWrapper>
          <HamburgerButton onClick={toggleMobileMenu} data-mobile-menu>
            {mobileMenuOpen ? <FaTimes /> : <FaBars />}
          </HamburgerButton>
        </UtilityIcons>
      </NavbarContainer>

      {/* Mobile Menu Sidesheet */}
      <MobileMenuBackdrop
        isOpen={mobileMenuOpen}
        onClick={() => setMobileMenuOpen(false)}
      />
      <MobileMenuSidesheet isOpen={mobileMenuOpen}>
        <MobileMenuCloseBtn
          onClick={() => setMobileMenuOpen(false)}
          aria-label="Close menu"
        >
          {FaTimes({}) as React.ReactElement}
        </MobileMenuCloseBtn>
        <MobileMenuContent>
          <MobileMenuSection>
            <MobileMenuSectionTitle>Navigation</MobileMenuSectionTitle>
            <MobileMenuActions>
              <MobileMenuAction
                to="/shop"
                onClick={() => setMobileMenuOpen(false)}
              >
                🛍️ Shop
              </MobileMenuAction>
              <MobileMenuAction
                to="/aboutus"
                onClick={() => setMobileMenuOpen(false)}
              >
                ℹ️ About Us
              </MobileMenuAction>
              <MobileMenuAction
                to="/contact"
                onClick={() => setMobileMenuOpen(false)}
              >
                📞 Contact
              </MobileMenuAction>
            </MobileMenuActions>
          </MobileMenuSection>

          <MobileMenuSection>
            <MobileMenuSectionTitle>Categories</MobileMenuSectionTitle>
            <MobileMenuCategories>
              <MobileMenuCategory
                to="/shop"
                onClick={() => setMobileMenuOpen(false)}
              >
                All Categories
              </MobileMenuCategory>
              {categories.map((category) => (
                <MobileMenuCategory
                  key={category}
                  to={`/shop?category=${encodeURIComponent(category)}`}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {category}
                </MobileMenuCategory>
              ))}
            </MobileMenuCategories>
          </MobileMenuSection>

          <MobileMenuSection>
            <MobileMenuSectionTitle>Account</MobileMenuSectionTitle>
            {user && (
              <MobileMenuUserInfo>
                <MobileMenuUserEmail>
                  {user.email || "User"}
                </MobileMenuUserEmail>
                <MobileMenuUserStatus>Signed in</MobileMenuUserStatus>
              </MobileMenuUserInfo>
            )}
            <MobileMenuActions>
              {user ? (
                <>
                  <MobileMenuAction
                    to="/orders"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    📦 My Orders
                  </MobileMenuAction>
                  <MobileMenuActionButton onClick={handleLogout}>
                    <SignOutButton />
                  </MobileMenuActionButton>
                </>
              ) : (
                <MobileMenuAction
                  to="/signin"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Sign In
                </MobileMenuAction>
              )}
            </MobileMenuActions>
          </MobileMenuSection>
        </MobileMenuContent>
      </MobileMenuSidesheet>

      {/* Side Sheet for User Account - All Screen Sizes */}
      <SideSheetOverlay isOpen={sideSheetOpen} onClick={closeSideSheet} />
      <SideSheet isOpen={sideSheetOpen} data-side-sheet>
        <SideSheetHeader isOpen={sideSheetOpen}>
          <SideSheetTitle>Account</SideSheetTitle>
          <CloseButton onClick={closeSideSheet}>
            <FaTimes />
          </CloseButton>
        </SideSheetHeader>

        {user && (
          <UserInfo isOpen={sideSheetOpen}>
            <UserEmail>{user.email || "User"}</UserEmail>
            <UserStatus>Signed in</UserStatus>
          </UserInfo>
        )}

        <SideSheetActions isOpen={sideSheetOpen}>
          <ActionButton
            variant="secondary"
            onClick={() => {
              navigate("/orders");
              closeSideSheet();
            }}
          >
            📦 My Orders
          </ActionButton>
          <LogoutButton onClick={handleLogout}>
            <SignOutButton />
          </LogoutButton>
        </SideSheetActions>
      </SideSheet>
    </>
  );
};

export default Navbar;
